import { api } from '../../core/network/api';

/**
 * Thin REST wrappers for the profile screen: the player's account card, their
 * title collection, and their leaderboard standing. No rendering or game logic lives here — the server is
 * the sole authority on what a player has earned or bought.
 *
 * Shapes are taken verbatim from game/title (ProfileView, TitleView) and
 * core/leaderboard (PersonalRank), not guessed.
 */

export type TitleSource = 'MISSION' | 'PREMIUM';
export type TitleRarity = 'MISSION' | 'COMMON' | 'RARE' | 'EPIC' | 'LEGENDARY';

/** One row of `GET /profile/titles` — a catalog entry plus this player's standing against it. */
export interface TitleView {
  id: string;
  label: string;
  source: TitleSource;
  rarity: TitleRarity;
  /** Delta-V price. Present on PREMIUM titles only. */
  price?: number;
  /** Human-readable unlock text. Present on MISSION titles only. */
  requirement?: string;
  /** Mission: its condition currently holds. Premium: it has been bought. */
  owned: boolean;
  equipped: boolean;
}

/** `GET /profile` — the User columns `/auth/me` cannot carry. */
export interface ProfileView {
  username: string | null;
  email: string;
  /** ISO timestamp — the card's "Date joined". */
  createdAt: string;
  level: number;
  xp: number;
  titleId: string | null;
  titleLabel: string | null;
  titleRarity: TitleRarity | null;
  /** Σ per colony of tier*1000 + seasonPoints. The card's "Colony Power". */
  colonyPower: number;
}

/**
 * `GET /leaderboard/colony-power/me` — core's PersonalRank.
 *
 * `colony-power` is the only category this game registers
 * (game/leaderboard-feed), and it is the live one: `User.rankPoints`, which
 * core's own season endpoints report, has no game-side writer at all and
 * would read 0 forever.
 */
export interface PersonalRank {
  rank: number;
  value: number;
  total: number;
}

export function fetchProfile(): Promise<ProfileView> {
  return api.get<ProfileView>('/profile');
}

export function fetchTitles(): Promise<TitleView[]> {
  return api.get<TitleView[]>('/profile/titles');
}

/** Equip a title the player owns, or pass null to show none. */
export function equipTitle(titleId: string | null): Promise<{ titleId: string | null }> {
  return api.patch<{ titleId: string | null }>('/profile/title', { titleId });
}

export function fetchColonyPowerRank(): Promise<PersonalRank> {
  return api.get<PersonalRank>('/leaderboard/colony-power/me');
}

/** Buy a premium title with Delta-V. Mirrors the cosmetic purchase endpoint. */
export function purchaseTitle(skuId: string): Promise<{ titleId: string }> {
  return api.post<{ titleId: string }>('/store/title', { skuId });
}
